// ATTICUS-HOME-DASHBOARD-REDESIGN-1 (2026-06-09) — "Quick Access" (formerly the
// "Jump back in" wireframe). The Home hero: PINNED workspaces render as premium
// launch tiles (the saved launchpad); RECENT is a compact, secondary history
// list. Both launch through the nav spine (locFromPref → navigate), so report /
// invoice / claim pins reopen their EXACT context (granular, per REPORTS-UX-1).
import { useNavigation } from "./NavigationContext";
import { locFromPref } from "./navRegistry";

// Per-kind glyph + accent hue — reads as intentional, not a debug enum.
const KIND = {
  claim:       { icon: "📄", hue: "#3b6fd4" },
  report_view: { icon: "📊", hue: "#7c3aed" },
  report:      { icon: "📊", hue: "#7c3aed" },
  invoice:     { icon: "🧾", hue: "#0891b2" },
  company:     { icon: "🏢", hue: "#0d9488" },
  contact:     { icon: "👤", hue: "#d97706" },
  page:        { icon: "▸",  hue: "#64748b" },
};
const kindOf = (k) => KIND[k] || KIND.page;
const QA_LABEL = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 };

function PinTile({ pin, onClick }) {
  const { icon, hue } = kindOf(pin.kind);
  return (
    <button onClick={onClick} title={pin.label}
      style={{
        display: "flex", alignItems: "center", gap: 11, textAlign: "left",
        flex: "1 1 calc(50% - 5px)", minWidth: 150, maxWidth: 280,
        padding: "11px 13px", border: "1px solid var(--border)", borderRadius: 9,
        background: "#fff", cursor: "pointer", boxShadow: "var(--shadow)",
        transition: "transform .12s, box-shadow .12s, border-color .12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "var(--shadow2)"; e.currentTarget.style.borderColor = hue; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "var(--shadow)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
      <span style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, fontSize: 15,
        display: "flex", alignItems: "center", justifyContent: "center", background: hue + "18", color: hue }}>{icon}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--navy)", lineHeight: 1.3,
        overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{pin.label}</span>
    </button>
  );
}

function RecentRow({ r, onClick }) {
  const { icon, hue } = kindOf(r.kind);
  return (
    <button onClick={onClick} title={r.label}
      style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left",
        padding: "5px 6px", fontSize: 12.5, background: "none", border: "none", borderRadius: 6,
        cursor: "pointer", color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.4 }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg3)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "none")}>
      <span style={{ fontSize: 12, width: 16, textAlign: "center", flexShrink: 0, color: hue }}>{icon}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
    </button>
  );
}

export default function QuickAccess() {
  const { recents, pins, navigate } = useNavigation();
  const go = (pref) => navigate(locFromPref(pref));
  const hasPins = (pins || []).length > 0;
  const hasRecents = (recents || []).length > 0;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><span>⚡</span><h3>Quick Access</h3></div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 272px" }}>
        {/* LEFT — Pinned workspaces (the hero) */}
        <div style={{ padding: "14px 16px", borderRight: "1px solid var(--border)", minWidth: 0 }}>
          <div style={QA_LABEL}>Pinned Workspaces</div>
          {hasPins ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {pins.map((p) => <PinTile key={`${p.kind}:${p.ref_id}`} pin={p} onClick={() => go(p)} />)}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "var(--text3)", lineHeight: 1.55, maxWidth: 460, padding: "2px 0 4px" }}>
              <div style={{ fontWeight: 600, color: "var(--text2)", marginBottom: 2 }}>No pinned workspaces yet.</div>
              Pin reports, claims, invoices, and operational views with the <strong>☆</strong> button to build your personal launchpad.
            </div>
          )}
        </div>
        {/* RIGHT — Recent (secondary, dense) */}
        <div style={{ padding: "14px 16px", minWidth: 0 }}>
          <div style={QA_LABEL}>Recent</div>
          {hasRecents ? (
            // LD-DESIGN-013: capped + scrollable — Recent never drives the card's height.
            <div style={{ maxHeight: 138, overflowY: "auto" }}>{recents.slice(0, 12).map((r) => <RecentRow key={`${r.kind}:${r.ref_id}`} r={r} onClick={() => go(r)} />)}</div>
          ) : (
            <div style={{ fontSize: 12.5, color: "var(--text3)", padding: "2px 0" }}>Your recent work appears here.</div>
          )}
        </div>
      </div>
    </div>
  );
}
